import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    DialogContent,
    Slider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDialog } from 'muibox';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';
import { OnlyFansPostStory } from '../../../../types/onlyFansTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AutoStoriesEditorValue {
    canvasWidth: number;
    canvasHeight: number;
    crop: { x: number; y: number; width: number; height: number } | null;
    texts: Record<string, OnlyFansPostStory.TextItem>;
}

type Props = {
    open: boolean;
    baseImageUrl: string;
    initialValue: AutoStoriesEditorValue | null;
    onSave: (value: AutoStoriesEditorValue) => void;
    onClose: () => void;
};

interface EditorLayer extends OnlyFansPostStory.TextItem {
    id: string;
}

type CropRect = { x: number; y: number; width: number; height: number };

type CornerHandle = 'tl' | 'tr' | 'bl' | 'br';

type DragState =
    | {
          kind: 'move';
          layerId: string;
          startX: number;
          startY: number;
          startLeft: number;
          startTop: number;
          moved: boolean;
          wasSelected: boolean;
      }
    | { kind: 'resize'; layerId: string; corner: CornerHandle; fixedX: number; fixedY: number; angle: number }
    | { kind: 'rotate'; layerId: string; centerX: number; centerY: number; startAngle: number; startPointerAngle: number }
    | { kind: 'crop-move'; startX: number; startY: number; startCrop: CropRect }
    | { kind: 'crop-resize'; corner: CornerHandle; startCrop: CropRect; aspect: number };

// ---------------------------------------------------------------------------
// Pure helpers (geometry / defaults) - kept free of DOM & React state so they
// can be reasoned about (and unit tested) independently of the component.
// ---------------------------------------------------------------------------

const DEFAULT_CANVAS_WIDTH = 552;
const DEFAULT_CANVAS_HEIGHT = 828;
const DEFAULT_TEXT_WIDTH = 200;
const DEFAULT_TEXT_HEIGHT = 32;
const MIN_LAYER_WIDTH = 40;
const MIN_LAYER_HEIGHT = 24;
const MIN_CROP_SIZE = 20;
// A pointerdown/up pair with less canvas-space movement than this is a tap, not a drag.
const TAP_MOVE_THRESHOLD = 3;
const ROTATION_HANDLE_OFFSET = 28;
const SNAP_ANGLES_DEG = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
const SNAP_THRESHOLD_DEG = 4;
// Selection/rotation handles must paint above every text layer regardless of its zIndex.
const HANDLE_Z_INDEX = 999999;

// Ordered to match the Instagram-style reference strip: white first, then dark/neutral,
// then warm -> cool hues.
const BASIC_WEB_COLORS: { name: string; hex: string }[] = [
    { name: 'White', hex: '#FFFFFF' },
    { name: 'Black', hex: '#000000' },
    { name: 'Silver', hex: '#C0C0C0' },
    { name: 'Gray', hex: '#808080' },
    { name: 'Fuchsia', hex: '#FF00FF' },
    { name: 'Red', hex: '#FF0000' },
    { name: 'Maroon', hex: '#800000' },
    { name: 'Yellow', hex: '#FFFF00' },
    { name: 'Olive', hex: '#808000' },
    { name: 'Lime', hex: '#00FF00' },
    { name: 'Green', hex: '#008000' },
    { name: 'Aqua', hex: '#00FFFF' },
    { name: 'Teal', hex: '#008080' },
    { name: 'Blue', hex: '#0000FF' },
    { name: 'Navy', hex: '#000080' },
    { name: 'Purple', hex: '#800080' },
];

// The font set OnlyFans' own story editor offers (family/weight/text-transform extracted from
// their editor markup), so composed stories match the native OnlyFans look. All families are
// Google Fonts loaded as webfonts by BOTH the app (public/index.html) and the backend renderer
// (renderStory injects the same stylesheet), so the editor preview and the rendered video use
// identical glyphs regardless of what's installed on either machine. Every stack still ends in
// a generic family as a defensive fallback, and multi-word names use SINGLE quotes: the stack
// is interpolated into a double-quote-delimited style="..." attribute by the backend renderer
// (which also escapes double quotes defensively).
export interface StoryFontOption {
    id: string;
    label: string;
    fontFamily: string;
    fontWeight: '400' | '500' | '700' | '900';
    textTransform?: 'uppercase';
}

const FONT_OPTIONS: StoryFontOption[] = [
    { id: 'roboto', label: 'Roboto', fontFamily: 'Roboto, sans-serif', fontWeight: '400' },
    { id: 'roboto-medium', label: 'Roboto Medium', fontFamily: 'Roboto, sans-serif', fontWeight: '500' },
    { id: 'roboto-bold', label: 'Roboto Bold', fontFamily: 'Roboto, sans-serif', fontWeight: '700' },
    { id: 'pt-mono', label: 'PT Mono', fontFamily: "'PT Mono', monospace", fontWeight: '400' },
    { id: 'shantell-sans', label: 'Shantell Sans', fontFamily: "'Shantell Sans', cursive", fontWeight: '400' },
    { id: 'sofia-sans', label: 'Sofia Sans', fontFamily: "'Sofia Sans', sans-serif", fontWeight: '400', textTransform: 'uppercase' },
    { id: 'yanone-kaffeesatz', label: 'Yanone Kaffeesatz', fontFamily: "'Yanone Kaffeesatz', sans-serif", fontWeight: '700' },
    { id: 'rubik-medium', label: 'Rubik Medium', fontFamily: 'Rubik, sans-serif', fontWeight: '500' },
    { id: 'rubik-black', label: 'Rubik Black', fontFamily: 'Rubik, sans-serif', fontWeight: '900' },
];

// Matches a layer's stored font settings back to a picker option; unknown combinations (legacy
// stacks from the earlier curated list, bare 'Roboto', foreign metadata) select the default.
const findFontOption = (fontFamily: string, fontWeight: string): StoryFontOption =>
    FONT_OPTIONS.find(option => option.fontFamily === fontFamily && option.fontWeight === fontWeight) || FONT_OPTIONS[0];

// Red diagonal "none" strike, drawn with gradients so it needs no image asset.
const NONE_STRIKE_BACKGROUND =
    'linear-gradient(45deg, transparent 45%, red 45%, red 55%, transparent 55%), linear-gradient(-45deg, transparent 45%, red 45%, red 55%, transparent 55%)';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

// Maps a corner to its local direction from the box center: x is +1 for right-side corners,
// y is +1 for bottom-side corners.
const cornerSigns = (corner: CornerHandle): { x: 1 | -1; y: 1 | -1 } => ({
    x: corner === 'tr' || corner === 'br' ? 1 : -1,
    y: corner === 'bl' || corner === 'br' ? 1 : -1,
});

// Free container resize under rotation (transform-origin center): the dragged corner follows
// the pointer while the diagonally opposite corner stays FIXED in canvas space. The pointer's
// offset from the fixed corner is rotated into the layer's local frame (by -angle) to obtain
// the new w'/h' (clamped to a minimum; no maximum, no aspect lock), then the new center is
// fixed + R(angle)*(±w'/2, ±h'/2) and left/top are derived from that center.
const computeLayerResize = (
    fixed: { x: number; y: number },
    corner: CornerHandle,
    pointer: { x: number; y: number },
    angleDeg: number,
): { left: number; top: number; width: number; height: number } => {
    const signs = cornerSigns(corner);
    const local = rotateVector(pointer.x - fixed.x, pointer.y - fixed.y, -angleDeg);
    const width = Math.max(signs.x * local.x, MIN_LAYER_WIDTH);
    const height = Math.max(signs.y * local.y, MIN_LAYER_HEIGHT);
    const centerOffset = rotateVector((signs.x * width) / 2, (signs.y * height) / 2, angleDeg);
    const centerX = fixed.x + centerOffset.x;
    const centerY = fixed.y + centerOffset.y;

    return { left: centerX - width / 2, top: centerY - height / 2, width, height };
};

// Fits `contentWidth` x `contentHeight` inside `availWidth` x `availHeight` without upscaling -
// mirrors the `min(availW/canvasWidth, availH/canvasHeight, 1)` rule from the task brief.
const computeDisplayScale = (contentWidth: number, contentHeight: number, availWidth: number, availHeight: number): number => {
    if (availWidth <= 0 || availHeight <= 0 || contentWidth <= 0 || contentHeight <= 0) {
        return 1;
    }

    return Math.min(availWidth / contentWidth, availHeight / contentHeight, 1);
};

// Rotates the vector (x, y) by `angleDeg` using the same convention as CSS `transform:
// rotate(<angle>deg)` (clockwise-positive in a top-left-origin, Y-down coordinate system).
const rotateVector = (x: number, y: number, angleDeg: number): { x: number; y: number } => {
    const rad = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return { x: x * cos - y * sin, y: x * sin + y * cos };
};

// Angle (degrees) of the vector (dx, dy) about its origin. Only ever used for *differences*
// between two pointer angles, so the zero-direction convention is irrelevant as long as it
// is consistent and increases clockwise (matching CSS rotate() in a Y-down space).
const pointerAngleDeg = (dx: number, dy: number): number => (Math.atan2(dy, dx) * 180) / Math.PI;

// Wraps an angle into [-180, 180) so continuous rotation drags stay within the range the
// snap targets cover.
const normalizeAngleDeg = (angleDeg: number): number => (((angleDeg % 360) + 540) % 360) - 180;

const snapAngle = (angleDeg: number): number => {
    let nearest = angleDeg;
    let bestDelta = Infinity;

    SNAP_ANGLES_DEG.forEach(candidate => {
        const delta = Math.abs(angleDeg - candidate);
        if (delta < bestDelta) {
            bestDelta = delta;
            nearest = candidate;
        }
    });

    return bestDelta <= SNAP_THRESHOLD_DEG ? nearest : angleDeg;
};

// Converts a viewport pointer position into canvas-space px, undoing the wrapper's
// `transform: scale(displayScale)` (transform-origin 0 0).
const clientToLocalPoint = (
    clientX: number,
    clientY: number,
    wrapRect: { left: number; top: number },
    scale: number,
): { x: number; y: number } => ({
    x: (clientX - wrapRect.left) / scale,
    y: (clientY - wrapRect.top) / scale,
});

const computeDefaultCrop = (imageWidth: number, imageHeight: number, canvasWidth: number, canvasHeight: number): CropRect => {
    const targetAspect = canvasWidth / canvasHeight;
    const imageAspect = imageWidth / imageHeight;

    let width: number;
    let height: number;

    if (imageAspect > targetAspect) {
        height = imageHeight;
        width = height * targetAspect;
    } else {
        width = imageWidth;
        height = width / targetAspect;
    }

    return { x: (imageWidth - width) / 2, y: (imageHeight - height) / 2, width, height };
};

const moveCropRect = (startCrop: CropRect, deltaX: number, deltaY: number, imageWidth: number, imageHeight: number): CropRect => ({
    ...startCrop,
    x: clamp(startCrop.x + deltaX, 0, Math.max(0, imageWidth - startCrop.width)),
    y: clamp(startCrop.y + deltaY, 0, Math.max(0, imageHeight - startCrop.height)),
});

// Resizes `startCrop` by dragging `corner`, keeping the opposite corner fixed and the aspect
// ratio locked to `aspect`, clamped so the rect never leaves the source image bounds.
const computeCropResize = (
    startCrop: CropRect,
    corner: CornerHandle,
    pointer: { x: number; y: number },
    aspect: number,
    imageWidth: number,
    imageHeight: number,
): CropRect => {
    const anchor =
        corner === 'tl'
            ? { x: startCrop.x + startCrop.width, y: startCrop.y + startCrop.height }
            : corner === 'tr'
            ? { x: startCrop.x, y: startCrop.y + startCrop.height }
            : corner === 'bl'
            ? { x: startCrop.x + startCrop.width, y: startCrop.y }
            : { x: startCrop.x, y: startCrop.y };

    const sx = corner === 'tr' || corner === 'br' ? 1 : -1;
    const sy = corner === 'bl' || corner === 'br' ? 1 : -1;

    const maxWidthFromBounds = sx > 0 ? imageWidth - anchor.x : anchor.x;
    const maxHeightFromBounds = sy > 0 ? imageHeight - anchor.y : anchor.y;
    const maxWidth = Math.max(MIN_CROP_SIZE, Math.min(maxWidthFromBounds, maxHeightFromBounds * aspect));

    const desiredWidth = Math.max(Math.abs(pointer.x - anchor.x), Math.abs(pointer.y - anchor.y) * aspect);
    const width = clamp(desiredWidth, MIN_CROP_SIZE, maxWidth);
    const height = width / aspect;

    return {
        x: sx > 0 ? anchor.x : anchor.x - width,
        y: sy > 0 ? anchor.y : anchor.y - height,
        width,
        height,
    };
};

let layerIdCounter = 0;
const nextLayerId = (): string => {
    layerIdCounter += 1;

    return `layer-${Date.now().toString(36)}-${layerIdCounter}`;
};

const createDefaultLayer = (canvasWidth: number, canvasHeight: number, zIndex: number): EditorLayer => ({
    id: nextLayerId(),
    type: 'text',
    text: 'Text',
    left: Math.round(canvasWidth * 0.1),
    top: Math.round(canvasHeight * 0.4),
    textWidth: DEFAULT_TEXT_WIDTH,
    textHeight: DEFAULT_TEXT_HEIGHT,
    color: '#ffffff',
    bgColor: '',
    fontFamily: FONT_OPTIONS[0].fontFamily,
    fontSize: '24px',
    fontWeight: FONT_OPTIONS[0].fontWeight,
    textAlign: 'left',
    angle: 0,
    scale: 1,
    zIndex,
});

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

// Horizontal strip of round colour swatches for the on-canvas pill (Instagram-style):
// 32px circles, white ring on the selected one, optional leading "none" (struck-through)
// swatch, horizontally scrollable when it overflows the pill.
const ColorSwatchStrip = ({ value, onChange, allowNone }: { value: string; onChange: (hex: string) => void; allowNone?: boolean }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: 'auto', px: 0.5, py: 0.5 }}>
        {allowNone && (
            <Box
                onClick={() => onChange('')}
                role="button"
                aria-label="None"
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    flexShrink: 0,
                    cursor: 'pointer',
                    backgroundImage: NONE_STRIKE_BACKGROUND,
                    backgroundColor: '#ffffff',
                    border: value === '' ? '3px solid #ffffff' : '2px solid rgba(255,255,255,0.35)',
                    boxSizing: 'border-box',
                }}
            />
        )}
        {BASIC_WEB_COLORS.map(({ name, hex }) => (
            <Box
                key={hex}
                onClick={() => onChange(hex)}
                role="button"
                aria-label={name}
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    flexShrink: 0,
                    cursor: 'pointer',
                    bgcolor: hex,
                    border: value.toLowerCase() === hex.toLowerCase() ? '3px solid #ffffff' : '2px solid rgba(255,255,255,0.35)',
                    boxSizing: 'border-box',
                }}
            />
        ))}
    </Stack>
);

// Horizontal strip of round font chips for the on-canvas pill: each chip renders "Aa" in its
// own family/weight (the Sofia Sans chip shows "AA" naturally via its uppercase transform);
// the selected chip is a white circle with dark text.
const FontFamilyStrip = ({
    fontFamily,
    fontWeight,
    onChange,
}: {
    fontFamily: string;
    fontWeight: string;
    onChange: (option: StoryFontOption) => void;
}) => {
    const selectedOption = findFontOption(fontFamily, fontWeight);

    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: 'auto', px: 0.5, py: 0.5 }}>
            {FONT_OPTIONS.map(option => {
                const selected = option.id === selectedOption.id;

                return (
                    <Box
                        key={option.id}
                        onClick={() => onChange(option)}
                        role="button"
                        aria-label={option.label}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: selected ? '#ffffff' : 'rgba(255,255,255,0.15)',
                            color: selected ? '#111111' : '#ffffff',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: option.fontFamily,
                                fontWeight: option.fontWeight,
                                textTransform: option.textTransform,
                                fontSize: 16,
                                lineHeight: 1,
                            }}
                        >
                            Aa
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
};

// Font size slider for the on-canvas pill: small "Aa" at the left, large "Aa" at the right.
const FontSizeSliderPill = ({ value, onChange }: { value: number; onChange: (value: number) => void }) => (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: 280, maxWidth: '100%', px: 1 }}>
        <Typography sx={{ color: '#ffffff', fontSize: 12, lineHeight: 1, flexShrink: 0 }}>Aa</Typography>
        <Slider
            size="small"
            value={value}
            min={12}
            max={72}
            step={1}
            onChange={(_e, newValue) => onChange(Array.isArray(newValue) ? newValue[0] : newValue)}
            sx={{ color: '#ffffff', flex: 1 }}
        />
        <Typography sx={{ color: '#ffffff', fontSize: 28, lineHeight: 1, flexShrink: 0 }}>Aa</Typography>
    </Stack>
);

const TextLayerPropertyPanel = ({
    layer,
    canMoveUp,
    canMoveDown,
    onAlignChange,
    onMoveUp,
    onMoveDown,
    onDelete,
}: {
    layer: EditorLayer;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onAlignChange: (align: string) => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDelete: () => void;
}) => {
    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Alignment
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={layer.textAlign} onChange={(_e, value) => value && onAlignChange(value)}>
                    <ToggleButton value="left">Left</ToggleButton>
                    <ToggleButton value="center">Center</ToggleButton>
                    <ToggleButton value="right">Right</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Stack direction="row" spacing={1}>
                <Tooltip title="Send backward">
                    <span>
                        <Button size="small" variant="outlined" onClick={onMoveDown} disabled={!canMoveDown}>
                            <ArrowDownwardIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Bring forward">
                    <span>
                        <Button size="small" variant="outlined" onClick={onMoveUp} disabled={!canMoveUp}>
                            <ArrowUpwardIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={onDelete}>
                    Delete
                </Button>
            </Stack>
        </Stack>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SextforceAutoStoriesEditor = ({ open, baseImageUrl, initialValue, onSave, onClose }: Props) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dialog = useDialog();

    const canvasWidth = initialValue?.canvasWidth || DEFAULT_CANVAS_WIDTH;
    const canvasHeight = initialValue?.canvasHeight || DEFAULT_CANVAS_HEIGHT;

    const [layers, setLayers] = useState<EditorLayer[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    // Which on-canvas control pill is open (Instagram-style); only one at a time.
    const [activePill, setActivePill] = useState<'color' | 'bgColor' | 'fontSize' | 'fontFamily' | null>(null);
    const [crop, setCrop] = useState<CropRect | null>(null);
    const [cropMode, setCropMode] = useState(false);
    const [cropDraft, setCropDraft] = useState<CropRect | null>(null);
    const [imgNaturalSize, setImgNaturalSize] = useState<{ width: number; height: number } | null>(null);
    const [availSize, setAvailSize] = useState({ width: 0, height: 0 });
    const [dirty, setDirty] = useState(false);

    const stageRef = useRef<HTMLDivElement | null>(null);
    const canvasWrapRef = useRef<HTMLDivElement | null>(null);
    const cropWrapRef = useRef<HTMLDivElement | null>(null);
    const layerRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const dragStateRef = useRef<DragState | null>(null);
    // Freshly created layers whose stored dims still need to be replaced by the measured
    // content size once their DOM node exists.
    const pendingMeasureRef = useRef<Set<string>>(new Set());
    // Whether the next entry into inline edit mode should select the whole text (used by
    // "Add Text" so the default placeholder is immediately replaceable by typing).
    const selectAllOnEditRef = useRef(false);

    const prevOpenRef = useRef(false);

    // Reset all editing state only on the closed -> open transition, reading `initialValue`
    // at that moment. Keying on `initialValue` identity as well would silently discard the
    // user's in-progress edits whenever the parent re-renders with a new (but equal) object.
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        prevOpenRef.current = open;

        if (!open || wasOpen) {
            return;
        }

        if (initialValue) {
            const sortedEntries = Object.entries(initialValue.texts).sort((a, b) => a[1].zIndex - b[1].zIndex);
            setLayers(
                sortedEntries.map(([, item]) => {
                    // Normalise legacy scale-based layers so their VISUALS are preserved
                    // exactly while the editor continues with scale 1 (renderStory keeps
                    // honouring scale in its transform for rows saved before the free-resize
                    // model):
                    // - dims are baked to s*w x s*h, and because the box is laid out at
                    //   left/top with transform-origin center, left/top shift by
                    //   -(s-1)*dims/2 so the (scale-invariant) visual center stays put;
                    // - the numeric font size is multiplied by s so glyph size and text
                    //   wrapping match the old scaled rendering.
                    const scale = item.scale || 1;
                    const fontSizeValue = parseFloat(item.fontSize) || 24;

                    return {
                        ...item,
                        left: item.left - ((scale - 1) * item.textWidth) / 2,
                        top: item.top - ((scale - 1) * item.textHeight) / 2,
                        textWidth: item.textWidth * scale,
                        textHeight: item.textHeight * scale,
                        fontSize: `${fontSizeValue * scale}px`,
                        scale: 1,
                        id: nextLayerId(),
                    };
                }),
            );
            setCrop(initialValue.crop);
        } else {
            setLayers([]);
            setCrop(null);
        }

        setSelectedId(null);
        setEditingId(null);
        setActivePill(null);
        setCropMode(false);
        setCropDraft(null);
        setDirty(false);
    }, [open, initialValue]);

    // Preload the base image purely to learn its natural pixel dimensions - needed to compute
    // the default crop rect and to letterbox the image while crop mode is active.
    useEffect(() => {
        if (!open || !baseImageUrl) {
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (!cancelled) {
                setImgNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
            }
        };
        img.src = baseImageUrl;

        return () => {
            cancelled = true;
        };
    }, [open, baseImageUrl]);

    // Track the available space for the canvas so it can be scaled down to fit the viewport.
    useEffect(() => {
        if (!open) {
            return;
        }

        const node = stageRef.current;
        if (!node) {
            return;
        }

        const updateSize = () => setAvailSize({ width: node.clientWidth, height: node.clientHeight });
        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);

        return () => observer.disconnect();
    }, [open, fullScreen, cropMode]);

    const displayScale = computeDisplayScale(canvasWidth, canvasHeight, availSize.width, availSize.height);
    const cropDisplayScale = imgNaturalSize
        ? computeDisplayScale(imgNaturalSize.width, imgNaturalSize.height, availSize.width, availSize.height)
        : 1;

    const registerLayerRef = useCallback((id: string, node: HTMLDivElement | null) => {
        if (node) {
            layerRefs.current.set(id, node);
        } else {
            layerRefs.current.delete(id);
        }
    }, []);

    const selectedLayer = layers.find(layer => layer.id === selectedId) || null;
    const selectedIndex = selectedLayer ? layers.findIndex(layer => layer.id === selectedLayer.id) : -1;

    // Replace freshly created layers' default dims with their measured content size once the
    // DOM node exists (the queue is cleared before setLayers so this effect cannot loop).
    useEffect(() => {
        if (pendingMeasureRef.current.size === 0) {
            return;
        }

        const ids = Array.from(pendingMeasureRef.current);
        pendingMeasureRef.current.clear();

        setLayers(prev =>
            prev.map(layer => {
                if (!ids.includes(layer.id)) {
                    return layer;
                }
                const node = layerRefs.current.get(layer.id);
                if (!node) {
                    return layer;
                }

                return { ...layer, textWidth: node.offsetWidth, textHeight: node.offsetHeight };
            }),
        );
    }, [layers]);

    // -- Inline text editing --------------------------------------------------

    const enterEditMode = (layerId: string, selectAll: boolean) => {
        selectAllOnEditRef.current = selectAll;
        setSelectedId(layerId);
        setEditingId(layerId);
    };

    // Focus the inline textarea when it mounts, place the caret (or select-all after "Add
    // Text"), and size it to its content.
    const editTextareaRef = useCallback((node: HTMLTextAreaElement | null) => {
        if (!node) {
            return;
        }

        node.style.height = 'auto';
        node.style.height = `${node.scrollHeight}px`;

        if (document.activeElement !== node) {
            node.focus();
            if (selectAllOnEditRef.current) {
                node.select();
            } else {
                node.setSelectionRange(node.value.length, node.value.length);
            }
            selectAllOnEditRef.current = false;
        }
    }, []);

    const handleEditTextChange = (layerId: string, e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Auto-grow the textarea (canvas-space layout px; the display-scale transform on the
        // canvas wrapper does not affect scrollHeight).
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;

        setLayers(prev => prev.map(layer => (layer.id === layerId ? { ...layer, text: value } : layer)));
        setDirty(true);
    };

    // -- Text layer CRUD ----------------------------------------------------

    const handleAddText = () => {
        const layer = createDefaultLayer(canvasWidth, canvasHeight, 0);
        pendingMeasureRef.current.add(layer.id);
        // zIndex is assigned inside the functional update so it is always consistent with
        // the list length at update time.
        setLayers(prev => [...prev, { ...layer, zIndex: prev.length }]);
        enterEditMode(layer.id, true);
        setDirty(true);
    };

    const updateSelectedLayer = (updater: (layer: EditorLayer) => EditorLayer) => {
        if (!selectedId) {
            return;
        }
        setLayers(prev => prev.map(layer => (layer.id === selectedId ? updater(layer) : layer)));
        setDirty(true);
    };

    const handleDeleteSelected = () => {
        if (!selectedId) {
            return;
        }
        setLayers(prev => prev.filter(layer => layer.id !== selectedId).map((layer, index) => ({ ...layer, zIndex: index })));
        setSelectedId(null);
        setEditingId(null);
        setActivePill(null);
        setDirty(true);
    };

    const handleMoveSelected = (direction: 'up' | 'down') => {
        if (!selectedId) {
            return;
        }
        setLayers(prev => {
            const index = prev.findIndex(layer => layer.id === selectedId);
            const swapWith = direction === 'up' ? index + 1 : index - 1;
            if (index < 0 || swapWith < 0 || swapWith >= prev.length) {
                return prev;
            }
            const next = [...prev];
            [next[index], next[swapWith]] = [next[swapWith], next[index]];

            return next.map((layer, i) => ({ ...layer, zIndex: i }));
        });
        setDirty(true);
    };

    // -- Layer drag (move) ---------------------------------------------------

    const handleLayerPointerDown = (e: React.PointerEvent<HTMLDivElement>, layer: EditorLayer) => {
        if (cropMode) {
            return;
        }
        // While this layer is being inline-edited its textarea owns pointer interactions
        // (caret placement, text selection) - never start a drag.
        if (editingId === layer.id) {
            e.stopPropagation();

            return;
        }
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);

        const wasSelected = selectedId === layer.id;
        setSelectedId(layer.id);

        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }
        const point = clientToLocalPoint(e.clientX, e.clientY, wrapRect, displayScale);
        dragStateRef.current = {
            kind: 'move',
            layerId: layer.id,
            startX: point.x,
            startY: point.y,
            startLeft: layer.left,
            startTop: layer.top,
            moved: false,
            wasSelected,
        };
    };

    const handleLayerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragStateRef.current;
        if (!drag || drag.kind !== 'move') {
            return;
        }
        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }
        const point = clientToLocalPoint(e.clientX, e.clientY, wrapRect, displayScale);
        const deltaX = point.x - drag.startX;
        const deltaY = point.y - drag.startY;

        // Ignore sub-threshold jitter so a tap stays a tap (and can enter edit mode on
        // pointerup) instead of nudging the layer by a pixel.
        if (!drag.moved && Math.hypot(deltaX, deltaY) < TAP_MOVE_THRESHOLD) {
            return;
        }
        drag.moved = true;

        setLayers(prev =>
            prev.map(layer =>
                layer.id === drag.layerId ? { ...layer, left: drag.startLeft + deltaX, top: drag.startTop + deltaY } : layer,
            ),
        );
        setDirty(true);
    };

    // Shared end-of-drag handler, used for both pointerup and pointercancel (the browser
    // fires the latter when e.g. a touch gesture is taken over by scrolling or the OS).
    const handleHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        dragStateRef.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    };

    // Layer-body pointerup: a tap (no movement) on an already-selected layer enters inline
    // edit mode, Instagram-style. Anything else is just the end of a move drag.
    const handleLayerPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragStateRef.current;
        if (drag && drag.kind === 'move' && !drag.moved && drag.wasSelected) {
            enterEditMode(drag.layerId, false);
        }
        handleHandlePointerUp(e);
    };

    // Visual center of a layer in canvas space. With `transform-origin: center center` the
    // element rotates about the center of its own box, so C = (left + w0/2, top + h0/2) is
    // the transform pivot - the fixed point rotation gestures measure against. (Container
    // resizes recompute left/top explicitly, so C is only read at gesture start.)
    const layerCenter = (layer: EditorLayer): { x: number; y: number } => {
        const layerNode = layerRefs.current.get(layer.id);
        const w0 = layerNode?.offsetWidth ?? layer.textWidth;
        const h0 = layerNode?.offsetHeight ?? layer.textHeight;

        return { x: layer.left + w0 / 2, y: layer.top + h0 / 2 };
    };

    // -- Corner anchors (free container resize) -------------------------------

    const handleAnchorPointerDown = (e: React.PointerEvent<HTMLDivElement>, layer: EditorLayer, corner: CornerHandle) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);

        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }

        // The diagonally opposite corner stays fixed in canvas space for the whole gesture;
        // capture its visual (rotated) position now. Use the rendered box dims (offsetWidth/
        // offsetHeight) rather than the stored ones so an overflowing min-height box resizes
        // from where it actually is on screen.
        const layerNode = layerRefs.current.get(layer.id);
        const w0 = layerNode?.offsetWidth ?? layer.textWidth;
        const h0 = layerNode?.offsetHeight ?? layer.textHeight;
        const center = layerCenter(layer);
        const signs = cornerSigns(corner);
        const fixedOffset = rotateVector((-signs.x * w0) / 2, (-signs.y * h0) / 2, layer.angle);

        dragStateRef.current = {
            kind: 'resize',
            layerId: layer.id,
            corner,
            fixedX: center.x + fixedOffset.x,
            fixedY: center.y + fixedOffset.y,
            angle: layer.angle,
        };
    };

    const handleAnchorPointerMove = (e: React.PointerEvent<HTMLDivElement>, layer: EditorLayer) => {
        const drag = dragStateRef.current;
        if (!drag || drag.kind !== 'resize' || drag.layerId !== layer.id) {
            return;
        }
        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }

        const pointer = clientToLocalPoint(e.clientX, e.clientY, wrapRect, displayScale);
        const next = computeLayerResize({ x: drag.fixedX, y: drag.fixedY }, drag.corner, pointer, drag.angle);

        setLayers(prev =>
            prev.map(l =>
                l.id === layer.id ? { ...l, left: next.left, top: next.top, textWidth: next.width, textHeight: next.height } : l,
            ),
        );
        setDirty(true);
    };

    // -- Rotation handle ------------------------------------------------------

    const handleRotatePointerDown = (e: React.PointerEvent<HTMLDivElement>, layer: EditorLayer) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);

        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }

        // Measure pointer angles about the layer's visual center C - with `transform-origin:
        // center center` C is the transform pivot and is invariant under both rotate and
        // scale, so delta-from-start angles about it track the cursor exactly.
        const startPointer = clientToLocalPoint(e.clientX, e.clientY, wrapRect, displayScale);
        const center = layerCenter(layer);

        dragStateRef.current = {
            kind: 'rotate',
            layerId: layer.id,
            centerX: center.x,
            centerY: center.y,
            startAngle: layer.angle,
            startPointerAngle: pointerAngleDeg(startPointer.x - center.x, startPointer.y - center.y),
        };
    };

    const handleRotatePointerMove = (e: React.PointerEvent<HTMLDivElement>, layer: EditorLayer) => {
        const drag = dragStateRef.current;
        if (!drag || drag.kind !== 'rotate' || drag.layerId !== layer.id) {
            return;
        }
        const wrapRect = canvasWrapRef.current?.getBoundingClientRect();
        if (!wrapRect) {
            return;
        }
        const pointer = clientToLocalPoint(e.clientX, e.clientY, wrapRect, displayScale);
        const currentPointerAngle = pointerAngleDeg(pointer.x - drag.centerX, pointer.y - drag.centerY);
        const rawAngle = normalizeAngleDeg(drag.startAngle + (currentPointerAngle - drag.startPointerAngle));
        const angle = snapAngle(rawAngle);

        setLayers(prev => prev.map(l => (l.id === layer.id ? { ...l, angle } : l)));
        setDirty(true);
    };

    // -- On-canvas control pills ----------------------------------------------

    const togglePill = (pill: 'color' | 'bgColor' | 'fontSize' | 'fontFamily') => {
        setActivePill(prev => (prev === pill ? null : pill));
    };

    // Circular icon button for the on-canvas toolbar; the active one gets a white ring.
    const toolbarButtonSx = (active: boolean): React.CSSProperties => ({
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        border: active ? '2px solid #ffffff' : '2px solid transparent',
        boxSizing: 'border-box',
    });

    // -- Canvas background (deselect / exit edit mode) ------------------------

    const handleCanvasBackgroundPointerDown = () => {
        setSelectedId(null);
        setEditingId(null);
        setActivePill(null);
    };

    // -- Crop mode ------------------------------------------------------------

    const handleEnterCropMode = () => {
        if (!imgNaturalSize) {
            return;
        }
        setCropDraft(crop ?? computeDefaultCrop(imgNaturalSize.width, imgNaturalSize.height, canvasWidth, canvasHeight));
        setSelectedId(null);
        setEditingId(null);
        setActivePill(null);
        setCropMode(true);
    };

    const handleConfirmCrop = () => {
        setCrop(cropDraft);
        setCropMode(false);
        setDirty(true);
    };

    const handleCancelCropMode = () => {
        setCropMode(false);
        setCropDraft(null);
    };

    const handleResetCrop = () => {
        setCrop(null);
        setCropDraft(null);
        setCropMode(false);
        setDirty(true);
    };

    const clientToImagePoint = (clientX: number, clientY: number): { x: number; y: number } => {
        const rect = cropWrapRef.current?.getBoundingClientRect();
        if (!rect) {
            return { x: 0, y: 0 };
        }

        return clientToLocalPoint(clientX, clientY, rect, cropDisplayScale);
    };

    const handleCropRectPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!cropDraft) {
            return;
        }
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const point = clientToImagePoint(e.clientX, e.clientY);
        dragStateRef.current = { kind: 'crop-move', startX: point.x, startY: point.y, startCrop: cropDraft };
    };

    const handleCropRectPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragStateRef.current;
        if (!drag || drag.kind !== 'crop-move' || !imgNaturalSize) {
            return;
        }
        const point = clientToImagePoint(e.clientX, e.clientY);
        const deltaX = point.x - drag.startX;
        const deltaY = point.y - drag.startY;
        setCropDraft(moveCropRect(drag.startCrop, deltaX, deltaY, imgNaturalSize.width, imgNaturalSize.height));
    };

    const handleCropResizePointerDown = (e: React.PointerEvent<HTMLDivElement>, corner: CornerHandle) => {
        if (!cropDraft) {
            return;
        }
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStateRef.current = { kind: 'crop-resize', corner, startCrop: cropDraft, aspect: canvasWidth / canvasHeight };
    };

    const handleCropResizePointerMove = (e: React.PointerEvent<HTMLDivElement>, corner: CornerHandle) => {
        const drag = dragStateRef.current;
        if (!drag || drag.kind !== 'crop-resize' || drag.corner !== corner || !imgNaturalSize) {
            return;
        }
        const point = clientToImagePoint(e.clientX, e.clientY);
        setCropDraft(computeCropResize(drag.startCrop, corner, point, drag.aspect, imgNaturalSize.width, imgNaturalSize.height));
    };

    // -- Save / close ---------------------------------------------------------

    const handleSave = () => {
        const texts: Record<string, OnlyFansPostStory.TextItem> = {};

        // Container dims are authoritative layer state (no DOM measurement needed), and every
        // layer that passes through this editor is normalised to scale 1 - legacy scale
        // values are baked into textWidth/textHeight at load time.
        layers.forEach((layer, index) => {
            const { id, ...rest } = layer;
            texts[String(index)] = {
                ...rest,
                scale: 1,
                zIndex: index,
            };
        });

        onSave({ canvasWidth, canvasHeight, crop, texts });
        setDirty(false);
    };

    const handleRequestClose = () => {
        if (!dirty) {
            onClose();

            return;
        }

        dialog
            .confirm({
                title: 'Discard Changes',
                message: 'You have unsaved changes to this story. Are you sure you want to close without saving?',
                ok: { text: 'Discard', color: 'error', variant: 'contained' },
                cancel: { text: 'Keep Editing', color: 'primary', variant: 'outlined' },
            })
            .then(() => onClose())
            .catch(() => {});
    };

    // -- Selection handle rendering ---------------------------------------------

    const renderSelectionHandles = (layer: EditorLayer) => {
        const node = layerRefs.current.get(layer.id);
        const w0 = node?.offsetWidth ?? layer.textWidth;
        const h0 = node?.offsetHeight ?? layer.textHeight;

        // The handles are siblings of the transformed layer div (not children), so their
        // positions are computed explicitly: with `transform-origin: center center` a corner's
        // visual position is C + rotate(offsetFromCenter, angle), where the center
        // C = (left + w0/2, top + h0/2) is the rotation pivot. Editor layers always have
        // scale 1 (legacy scale is baked into the container dims at load), so the rendered
        // box dims are used directly.
        const centerX = layer.left + w0 / 2;
        const centerY = layer.top + h0 / 2;
        const halfW = w0 / 2;
        const halfH = h0 / 2;

        const corners: { corner: CornerHandle; local: { x: number; y: number }; cursor: string }[] = [
            { corner: 'tl', local: { x: -halfW, y: -halfH }, cursor: 'nwse-resize' },
            { corner: 'tr', local: { x: halfW, y: -halfH }, cursor: 'nesw-resize' },
            { corner: 'bl', local: { x: -halfW, y: halfH }, cursor: 'nesw-resize' },
            { corner: 'br', local: { x: halfW, y: halfH }, cursor: 'nwse-resize' },
        ];
        // Rotation handle: 28 canvas-px beyond the (rotated) top-centre edge.
        const rotateLocal = rotateVector(0, -halfH - ROTATION_HANDLE_OFFSET, layer.angle);

        return (
            <React.Fragment>
                {corners.map(({ corner, local, cursor }) => {
                    const p = rotateVector(local.x, local.y, layer.angle);

                    return (
                        <div
                            key={corner}
                            onPointerDown={e => handleAnchorPointerDown(e, layer, corner)}
                            onPointerMove={e => handleAnchorPointerMove(e, layer)}
                            onPointerUp={handleHandlePointerUp}
                            onPointerCancel={handleHandlePointerUp}
                            style={{
                                position: 'absolute',
                                left: centerX + p.x - 6,
                                top: centerY + p.y - 6,
                                width: 12,
                                height: 12,
                                background: theme.palette.primary.main,
                                border: '1px solid #ffffff',
                                boxSizing: 'border-box',
                                cursor,
                                zIndex: HANDLE_Z_INDEX,
                                touchAction: 'none',
                            }}
                        />
                    );
                })}
                <div
                    onPointerDown={e => handleRotatePointerDown(e, layer)}
                    onPointerMove={e => handleRotatePointerMove(e, layer)}
                    onPointerUp={handleHandlePointerUp}
                    onPointerCancel={handleHandlePointerUp}
                    style={{
                        position: 'absolute',
                        left: centerX + rotateLocal.x - 8,
                        top: centerY + rotateLocal.y - 8,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: theme.palette.secondary.main,
                        border: '1px solid #ffffff',
                        boxSizing: 'border-box',
                        cursor: 'grab',
                        zIndex: HANDLE_Z_INDEX,
                        touchAction: 'none',
                    }}
                />
            </React.Fragment>
        );
    };

    const cropCornerStyle = (corner: CornerHandle): React.CSSProperties => {
        switch (corner) {
            case 'tl':
                return { left: -7, top: -7, cursor: 'nwse-resize' };
            case 'tr':
                return { right: -7, top: -7, cursor: 'nesw-resize' };
            case 'bl':
                return { left: -7, bottom: -7, cursor: 'nesw-resize' };
            case 'br':
            default:
                return { right: -7, bottom: -7, cursor: 'nwse-resize' };
        }
    };

    return (
        <DialogAdaptive open={open} onClose={handleRequestClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
            <StyledCardTitleBar title="Story Editor" />

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddText} disabled={cropMode}>
                        Add Text
                    </Button>
                    {!cropMode ? (
                        <Button size="small" startIcon={<CropIcon />} onClick={handleEnterCropMode} disabled={!imgNaturalSize}>
                            Crop
                        </Button>
                    ) : (
                        <React.Fragment>
                            <Button size="small" variant="contained" onClick={handleConfirmCrop}>
                                Apply Crop
                            </Button>
                            <Button size="small" onClick={handleCancelCropMode}>
                                Cancel Crop
                            </Button>
                        </React.Fragment>
                    )}
                    {crop && !cropMode && (
                        <Button size="small" color="secondary" onClick={handleResetCrop}>
                            Reset Crop
                        </Button>
                    )}
                </Stack>

                <Stack direction={fullScreen ? 'column' : 'row'} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <Box
                        ref={stageRef}
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minHeight: fullScreen ? 260 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.900',
                            overflow: 'hidden',
                            p: 2,
                        }}
                    >
                        {cropMode && imgNaturalSize && cropDraft ? (
                            <div
                                ref={cropWrapRef}
                                style={{
                                    position: 'relative',
                                    width: imgNaturalSize.width,
                                    height: imgNaturalSize.height,
                                    transform: `scale(${cropDisplayScale})`,
                                    transformOrigin: '0 0',
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src={baseImageUrl}
                                    alt=""
                                    style={{ display: 'block', width: imgNaturalSize.width, height: imgNaturalSize.height }}
                                    draggable={false}
                                />
                                <div
                                    onPointerDown={handleCropRectPointerDown}
                                    onPointerMove={handleCropRectPointerMove}
                                    onPointerUp={handleHandlePointerUp}
                                    onPointerCancel={handleHandlePointerUp}
                                    style={{
                                        position: 'absolute',
                                        left: cropDraft.x,
                                        top: cropDraft.y,
                                        width: cropDraft.width,
                                        height: cropDraft.height,
                                        border: '2px dashed #ffffff',
                                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                                        cursor: 'move',
                                        touchAction: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {(['tl', 'tr', 'bl', 'br'] as CornerHandle[]).map(corner => (
                                        <div
                                            key={corner}
                                            onPointerDown={e => handleCropResizePointerDown(e, corner)}
                                            onPointerMove={e => handleCropResizePointerMove(e, corner)}
                                            onPointerUp={handleHandlePointerUp}
                                            onPointerCancel={handleHandlePointerUp}
                                            style={{
                                                position: 'absolute',
                                                ...cropCornerStyle(corner),
                                                width: 14,
                                                height: 14,
                                                background: '#ffffff',
                                                border: `2px solid ${theme.palette.primary.main}`,
                                                boxSizing: 'border-box',
                                                touchAction: 'none',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div
                                ref={canvasWrapRef}
                                onPointerDown={handleCanvasBackgroundPointerDown}
                                style={{
                                    position: 'relative',
                                    width: canvasWidth,
                                    height: canvasHeight,
                                    transform: `scale(${displayScale})`,
                                    transformOrigin: '0 0',
                                    overflow: 'hidden',
                                    background: '#000000',
                                    flexShrink: 0,
                                }}
                            >
                                {crop ? (
                                    <img
                                        src={baseImageUrl}
                                        alt=""
                                        draggable={false}
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            transform: `scale(${canvasWidth / crop.width}, ${
                                                canvasHeight / crop.height
                                            }) translate(${-crop.x}px, ${-crop.y}px)`,
                                            transformOrigin: '0 0',
                                            zIndex: 0,
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={baseImageUrl}
                                        alt=""
                                        draggable={false}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            zIndex: 0,
                                        }}
                                    />
                                )}

                                {layers.map(layer => (
                                    <div
                                        key={layer.id}
                                        ref={node => registerLayerRef(layer.id, node)}
                                        onPointerDown={e => handleLayerPointerDown(e, layer)}
                                        onPointerMove={handleLayerPointerMove}
                                        onPointerUp={handleLayerPointerUp}
                                        onPointerCancel={handleHandlePointerUp}
                                        onDoubleClick={() => !cropMode && enterEditMode(layer.id, false)}
                                        style={{
                                            position: 'absolute',
                                            left: layer.left,
                                            top: layer.top,
                                            width: layer.textWidth,
                                            // min-height (not height) so overflowing text grows
                                            // the box downwards instead of clipping - the
                                            // backend renderer uses the same rule.
                                            minHeight: layer.textHeight,
                                            boxSizing: 'border-box',
                                            transform: `rotate(${layer.angle}deg) scale(${layer.scale})`,
                                            transformOrigin: 'center center',
                                            color: layer.color,
                                            fontFamily: layer.fontFamily,
                                            fontSize: layer.fontSize,
                                            fontWeight: layer.fontWeight as React.CSSProperties['fontWeight'],
                                            textAlign: layer.textAlign as React.CSSProperties['textAlign'],
                                            // Inherited by the inline-editing textarea automatically.
                                            textTransform: layer.textTransform as React.CSSProperties['textTransform'],
                                            zIndex: layer.zIndex,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            background: layer.bgColor || undefined,
                                            padding: layer.bgColor ? '4px 8px' : undefined,
                                            borderRadius: layer.bgColor ? 4 : undefined,
                                            outline: layer.id === selectedId ? '1px dashed #29b6f6' : undefined,
                                            outlineOffset: layer.id === selectedId ? 2 : undefined,
                                            cursor: layer.id === editingId ? 'text' : 'move',
                                            touchAction: layer.id === editingId ? 'auto' : 'none',
                                            userSelect: 'none',
                                        }}
                                    >
                                        {layer.id === editingId ? (
                                            <textarea
                                                ref={editTextareaRef}
                                                value={layer.text}
                                                rows={1}
                                                onChange={e => handleEditTextChange(layer.id, e)}
                                                onBlur={() => setEditingId(null)}
                                                onPointerDown={e => e.stopPropagation()}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    border: 'none',
                                                    outline: 'none',
                                                    resize: 'none',
                                                    padding: 0,
                                                    margin: 0,
                                                    background: 'transparent',
                                                    color: 'inherit',
                                                    font: 'inherit',
                                                    textAlign: 'inherit',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    overflow: 'hidden',
                                                    userSelect: 'text',
                                                    touchAction: 'auto',
                                                }}
                                            />
                                        ) : (
                                            layer.text
                                        )}
                                    </div>
                                ))}

                                {selectedLayer && selectedLayer.id !== editingId && renderSelectionHandles(selectedLayer)}
                            </div>
                        )}

                        {/* Instagram-style on-canvas controls: icon toolbar (top) + one active pill
                            (bottom). Overlaid on the stage (not the scaled canvas) so they render at
                            screen scale; conditionally mounted so they never block layer dragging
                            when hidden. preventDefault on pointerdown keeps the inline-editing
                            textarea focused while restyling. */}
                        {!cropMode && selectedLayer && (
                            <React.Fragment>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    onPointerDown={e => e.preventDefault()}
                                    sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 20 }}
                                >
                                    <Box
                                        role="button"
                                        aria-label="Text color"
                                        onClick={() => togglePill('color')}
                                        sx={toolbarButtonSx(activePill === 'color')}
                                    >
                                        <Box
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                bgcolor: selectedLayer.color,
                                                border: '2px solid #ffffff',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    </Box>
                                    <Box
                                        role="button"
                                        aria-label="Background color"
                                        onClick={() => togglePill('bgColor')}
                                        sx={toolbarButtonSx(activePill === 'bgColor')}
                                    >
                                        {selectedLayer.bgColor ? (
                                            <Box
                                                sx={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '4px',
                                                    bgcolor: selectedLayer.bgColor,
                                                    border: '2px solid #ffffff',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    backgroundImage: NONE_STRIKE_BACKGROUND,
                                                    backgroundColor: '#ffffff',
                                                    border: '2px solid #ffffff',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box
                                        role="button"
                                        aria-label="Font size"
                                        onClick={() => togglePill('fontSize')}
                                        sx={toolbarButtonSx(activePill === 'fontSize')}
                                    >
                                        <Typography sx={{ color: '#ffffff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>Aa</Typography>
                                    </Box>
                                    <Box
                                        role="button"
                                        aria-label="Font family"
                                        onClick={() => togglePill('fontFamily')}
                                        sx={toolbarButtonSx(activePill === 'fontFamily')}
                                    >
                                        <Typography sx={{ color: '#ffffff', fontSize: 13, fontFamily: 'Georgia, serif', lineHeight: 1 }}>
                                            Aa
                                        </Typography>
                                    </Box>
                                </Stack>

                                {activePill && (
                                    <Box
                                        onPointerDown={e => {
                                            // Keep the inline-editing textarea focused when tapping
                                            // swatches/chips, but let pointerdown proceed inside the
                                            // slider: cancelling it suppresses the compatibility
                                            // mousedown that MUI's Slider drives its mouse
                                            // interaction from (touch survives via native
                                            // touchstart, but mouse drag would be dead).
                                            if (!(e.target as HTMLElement).closest('.MuiSlider-root')) {
                                                e.preventDefault();
                                            }
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            maxWidth: '92%',
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '12px',
                                            px: 1.5,
                                            py: 1,
                                            boxShadow: 4,
                                            zIndex: 20,
                                        }}
                                    >
                                        {activePill === 'color' && (
                                            <ColorSwatchStrip
                                                value={selectedLayer.color}
                                                onChange={hex => updateSelectedLayer(layer => ({ ...layer, color: hex }))}
                                            />
                                        )}
                                        {activePill === 'bgColor' && (
                                            <ColorSwatchStrip
                                                value={selectedLayer.bgColor}
                                                onChange={hex => updateSelectedLayer(layer => ({ ...layer, bgColor: hex }))}
                                                allowNone
                                            />
                                        )}
                                        {activePill === 'fontSize' && (
                                            <FontSizeSliderPill
                                                value={parseInt(selectedLayer.fontSize, 10) || 24}
                                                onChange={value => updateSelectedLayer(layer => ({ ...layer, fontSize: `${value}px` }))}
                                            />
                                        )}
                                        {activePill === 'fontFamily' && (
                                            <FontFamilyStrip
                                                fontFamily={selectedLayer.fontFamily}
                                                fontWeight={selectedLayer.fontWeight}
                                                onChange={option =>
                                                    updateSelectedLayer(layer => ({
                                                        ...layer,
                                                        fontFamily: option.fontFamily,
                                                        fontWeight: option.fontWeight,
                                                        textTransform: option.textTransform,
                                                    }))
                                                }
                                            />
                                        )}
                                    </Box>
                                )}
                            </React.Fragment>
                        )}
                    </Box>

                    <Box
                        sx={{
                            width: fullScreen ? '100%' : 220,
                            flexShrink: 0,
                            borderLeft: fullScreen ? 0 : 1,
                            borderTop: fullScreen ? 1 : 0,
                            borderColor: 'divider',
                            overflowY: 'auto',
                            p: 2,
                        }}
                    >
                        {cropMode ? (
                            <Typography variant="body2" color="text.secondary">
                                Drag the rectangle to reposition the crop, or drag a corner handle to resize it. The aspect ratio is locked
                                to the story canvas.
                            </Typography>
                        ) : selectedLayer ? (
                            <TextLayerPropertyPanel
                                layer={selectedLayer}
                                canMoveUp={selectedIndex < layers.length - 1}
                                canMoveDown={selectedIndex > 0}
                                onAlignChange={align => updateSelectedLayer(layer => ({ ...layer, textAlign: align }))}
                                onMoveUp={() => handleMoveSelected('up')}
                                onMoveDown={() => handleMoveSelected('down')}
                                onDelete={handleDeleteSelected}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Select a text layer to edit its properties, tap a selected layer to edit its text, or tap "Add Text" to
                                create one.
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleRequestClose} startIcon={<CloseIcon />}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={cropMode}>
                    Save
                </Button>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceAutoStoriesEditor;
